const clamp01 = (v) => Math.max(0, Math.min(1, v));

export function normalizeHex(hex) {
  if (!hex || typeof hex !== "string") return null;
  let h = hex.trim().toUpperCase().replace(/^#/, "");
  if (h.length === 3 || h.length === 4) h = h.split("").map(c => c + c).join("");
  if (h.length !== 6 && h.length !== 8) return null;
  return `#${h}`;
}

function hexToRgb(hex) {
  const n = normalizeHex(hex);
  if (!n) return null;
  const h = n.slice(1);
  const r = parseInt(h.slice(0,2), 16);
  const g = parseInt(h.slice(2,4), 16);
  const b = parseInt(h.slice(4,6), 16);
  const a = h.length === 8 ? parseInt(h.slice(6,8), 16) / 255 : 1;
  return { r, g, b, a };
}
function rgbToHex({ r, g, b, a = 1 }) {
  const H = (x) => Math.round(x).toString(16).padStart(2, "0");
  const base = `#${H(r)}${H(g)}${H(b)}`;
  return a < 1 ? `${base}${H(a * 255)}` : base;
}

export const DARKEN_OVERRIDES = new Map([
  ["#F2DCD8", "#AA3833"],
  ["#F8D8B8", "#864100"],
  ["#F9EBB7", "#8D5E00"],
  ["#EAEBC9", "#817C03"],
  ["#DDEBC9", "#598B0E"],
  ["#A3F1CB", "#1D764B"],
  ["#B0E5F5", "#1E6379"],
  ["#CAD5F8", "#012FB5"],
  ["#D7C8F7", "#5013D5"],
  ["#ECCEF0", "#8B009D"],
  ["#F7D2DF", "#A30039"],
]);

export function setDarkenOverride(baseHex, targetHex) {
  const base = normalizeHex(baseHex);
  const target = normalizeHex(targetHex);
  if (base && target) DARKEN_OVERRIDES.set(base, target);
}

export function mix(hexA, hexB, weight = 0.5) {
  const a = hexToRgb(hexA), b = hexToRgb(hexB);
  if (!a || !b) return hexA || hexB || "#000000";
  const w = clamp01(weight);
  return rgbToHex({
    r: a.r * (1 - w) + b.r * w,
    g: a.g * (1 - w) + b.g * w,
    b: a.b * (1 - w) + b.b * w,
  });
}

function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return { h: 0, s: 0, l: 0 };
  let { r, g, b } = rgb;
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s; const l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > .5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}
function hslToHex(h, s, l) {
  s /= 100; l /= 100;
  const k = n => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = n => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  const H = (x) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${H(f(0))}${H(f(8))}${H(f(4))}`;
}

export function darkenHsl(hex, amt = 0.25) {
  const { h, s, l } = hexToHsl(hex);
  const newL = Math.max(0, l * (1 - amt));
  return hslToHex(h, s, newL);
}

export function darken(hex, { fallbackMode = 'hsl', mixStrength = 0.6, amt = 0.3 } = {}) {
  const base = normalizeHex(hex);
  if (!base) return "#333333";

  const override = DARKEN_OVERRIDES.get(base);
  if (override) return override;

  if (fallbackMode === 'mix') {
    return mix(base, "#000000", clamp01(mixStrength));
  }
  return darkenHsl(base, amt);
}

export function getContrastingText(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#000000";
  const luma = 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
  return luma > 160 ? "#000000" : "#FFFFFF";
}
export function withAlpha(hex, alpha = 1) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "rgba(0,0,0,0.2)";
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${clamp01(alpha)})`;
}